import React, { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import jobTitleApi from '../../../app/api/jobTitleApi';

const CombineJobTitlesPage = () => {
  const auth = useAuth();
  const { account } = useSelector((state) => state.app);
  const navigate = useNavigate();
  const [jobTitles, setJobTitles] = useState([]);
  const [selectedJobTitles, setSelectedJobTitles] = useState([]);
  const [newJobTitleName, setNewJobTitleName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (account?.id) {
      loadJobTitles();
    }
  }, [account]);

  const loadJobTitles = async () => {
    try {
      const response = await jobTitleApi.getJobTitles(auth.user.access_token, account.id);
      setJobTitles(response.data);
      if (!response.data || response.data.length === 0) {
        setError('No job titles found for this account.');
      } else {
        setError(null);
      }
    } catch (error) {
      console.error('Error loading job titles:', error);
      setError('Failed to load job titles. Please check your connection or contact support.');
    }
  };

  const handleJobTitleSelect = (jobTitleId) => {
    setSelectedJobTitles(prev => {
      if (prev.includes(jobTitleId)) {
        return prev.filter(id => id !== jobTitleId);
      }
      return [...prev, jobTitleId];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedJobTitles.length < 2) {
      setError('Please select at least 2 job titles to combine');
      return;
    }
    if (!newJobTitleName.trim()) {
      setError('Please enter a new job title name');
      return;
    }

    setIsLoading(true);
    try {
      await jobTitleApi.combineJobTitles(auth.user.access_token, account.id, {
        jobTitleIds: selectedJobTitles,
        newJobTitleName: newJobTitleName.trim()
      });
      navigate('/sites');
    } catch (error) {
      console.error('Error combining job titles:', error);
      setError('Failed to combine job titles. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Combine Job Titles
      </Typography>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}
      <form onSubmit={handleSubmit}>
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Select Job Titles to Combine
          </Typography>
          <FormGroup>
            {jobTitles.map(jobTitle => (
              <FormControlLabel
                key={jobTitle.id}
                control={
                  <Checkbox
                    checked={selectedJobTitles.includes(jobTitle.id)}
                    onChange={() => handleJobTitleSelect(jobTitle.id)}
                  />
                }
                label={`${jobTitle.name} (${jobTitle.profileCount})`}
              />
            ))}
          </FormGroup>
        </FormControl>

        <TextField
          fullWidth
          label="New Job Title Name"
          value={newJobTitleName}
          onChange={(e) => setNewJobTitleName(e.target.value)}
          sx={{ mb: 3 }}
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isLoading || selectedJobTitles.length < 2 || !newJobTitleName.trim()}
        >
          {isLoading ? 'Combining...' : 'Combine Job Titles'}
        </Button>
      </form>
    </Box>
  );
};

export default CombineJobTitlesPage; 